// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { CustomAppState } from "@bg/App/Store/Reducers";
import Tooltip from "@bg/MobiusOverrides/Tooltip/Tooltip";
import { screen } from "@bg/XDL/Helpers";
import { styledLinksMenu } from "@bg/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import Clickable from "@insite/mobius/Clickable/Clickable";
import Link, { LinkProps, StyledTypography } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { getProp } from "@insite/mobius/utilities";
import applyPropBuilder from "@insite/mobius/utilities/applyPropBuilder";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import { StyledProp } from "@insite/mobius/utilities/InjectableCss";
import injectCss from "@insite/mobius/utilities/injectCss";
import MobiusStyledComponentProps from "@insite/mobius/utilities/MobiusStyledComponentProps";
import parse from "html-react-parser";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import styled, { css, withTheme } from "styled-components";

const mapStateToProps = (state: CustomAppState) => {
    const pageType = getCurrentPage(state).type; // CheckoutReviewAndSubmitPage || CheckoutShippingPage ||

    return {
        pageType,
        homeUrl: getPageLinkByPageType(state, "HomePage")?.url,
        globalMessage: state.xHeader.globalMessage,
        orderHistoryPage: getPageLinkByPageType(state, "OrderHistoryPage"),
    };
};

const mapDispatchToProps = {};

export type BreadcrumbsProps = MobiusStyledComponentProps<
    "nav",
    {
        /** CSS string or styled-components function to be injected into this component.
         * @themable */
        css?: StyledProp<BreadcrumbsProps>;
        /** An array of link props */
        links?: LinkProps[];
        /**
         * Optional props to be passed down to all Link and Typography components.
         * Can be overriden by individual typographyProps in the links array.
         * @themable */
        typographyProps?: TypographyPresentationProps;
    }
>;

export type BreadcrumbsPresentationProps = Omit<BreadcrumbsProps, "links">;

const Slash = styled(Typography as any).attrs({ children: " " })`
    margin: 0 10px 0 10px;
    font-size: 12px;
    display: flex;
    &&:after {
        content: " ";
        width: 4px;
        height: 4px;
        background: ${getColor("common.ui2")};
        border-radius: 50%;
    }
`;

const StyledNav = styled.nav.attrs({ "aria-label": "breadcrumbs" })`
    display: flex;
    flex-direction: row;
    align-items: center;
    flex-wrap: wrap;
    @media print {
        display: none;
    }
    &.margin-class {
        ${screen("lte", "md")`
            margin-top: 60px !important;
        `}
    }
    ${injectCss}
`;

type Props = BreadcrumbsProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasHistory & {
        /**
         * Max number of Links that should be available to overflow
         */
        maxNoOfLinks?: number;
    };

const Breadcrumbs: React.FC<Props> = withTheme(
    ({ links, maxNoOfLinks, history, pageType, homeUrl, globalMessage, orderHistoryPage, ...otherProps }) => {
        const [isActive, setActive] = React.useState(false);
        const { applyProp, spreadProps } = applyPropBuilder(otherProps, { component: "breadcrumbs" });
        const typographyProps = spreadProps("typographyProps");
        // return null if `links` is an empty empty object, collection, map, or set.
        if (!links || links.length === 0) {
            return null;
        }

        const children = links.map((link: any, index: number) => {
            const children = link.children;
            // yield null if no children
            if (!React.Children.count(children)) {
                return null;
            }
            const key = typeof children === "string" ? `${children}_${index}` : index;
            if (index === links.length - 1) {
                return (
                    <Typography
                        css={css`
                            font-size: 13px;
                            line-height: 22px;
                            @media (max-width: 1024px) {
                                max-width: 230px;
                                display: block;
                                overflow: hidden;
                                white-space: nowrap;
                                height: 22px;
                                text-overflow: ellipsis;
                            }
                        `}
                        {...typographyProps}
                        aria-current="page"
                        key={key}
                        data-test-selector={`breadCrumb_${children}`}
                    >
                        {children}
                    </Typography>
                );
            }
            // XNG-Change: get filters values when going back via breadcrumbs -- BG-2603
            const isOrderHistoryLink = link.href === orderHistoryPage?.url;
            return (
                <React.Fragment key={key}>
                    <Link
                        typographyProps={typographyProps}
                        color="seconadry.main"
                        css={css`
                            z-index: 9;
                            font-size: 13px;
                            line-height: 22px;
                        `}
                        {...link}
                        // XNG-Change: get filters values when going back via breadcrumbs -- BG-2603
                        {...(isOrderHistoryLink &&
                        typeof window !== "undefined" &&
                        !!window.sessionStorage.getItem("orderHistoryLastUrl")
                            ? {
                                  ...{
                                      onClick: () => {
                                          history.push(window.sessionStorage.getItem("orderHistoryLastUrl"));
                                          window.sessionStorage.removeItem("orderHistoryLastUrl");
                                      },
                                      href: undefined,
                                  },
                              }
                            : "")}
                    />
                    <Slash {...typographyProps} />
                </React.Fragment>
            );
        });

        // return null if `links` map yields an array of nulls
        if (Object.keys(children.filter((x: typeof Link) => x !== null)).length === 0) {
            return null;
        }

        const tooltipContent = !isActive && children.length >= maxNoOfLinks && children.slice(3, children.length - 1);

        if (tooltipContent && tooltipContent.length > 0) {
            return (
                <StyledNav {...otherProps} css={applyProp("css")} className={globalMessage && "margin-class"}>
                    {children[0]}
                    {children.length <= 4 && children[1]}
                    {children.length <= 4 && children[2]}
                    <Tooltip
                        text={children.slice(1, children.length - 1)}
                        trigger="mouseenter focus"
                        cssOverrides={{
                            tooltipContainer: css`
                                ${styledLinksMenu(180)}
                                ${Slash as any} {
                                    display: none;
                                }
                                a {
                                    &:hover,
                                    &focus,
                                    &:active {
                                        &,
                                        span {
                                            font-size: 13px;
                                            line-height: 22px;
                                        }
                                    }
                                }
                            `,
                        }}
                        placement="bottom-start"
                        triggerComponent={
                            <Clickable data-test-selector="breadcrumbs_menu">
                                <Typography {...typographyProps} aria-current="page" key="otherBreadcrumbs">
                                    ...
                                </Typography>
                            </Clickable>
                        }
                    ></Tooltip>
                    <Slash {...typographyProps} />
                    {children[children.length - 1]}
                </StyledNav>
            );
        }

        return (
            <>
                {pageType === "CheckoutReviewAndSubmitPage" ||
                pageType === "CheckoutShippingPage" ||
                pageType === "OrderConfirmationPage" ? (
                    <StyledNav {...otherProps} css={applyProp("css")} className={globalMessage && "margin-class"}>
                        <Link href={homeUrl}>{translate("Continue Shopping")}</Link>
                    </StyledNav>
                ) : (
                    <StyledNav {...otherProps} css={applyProp("css")} className={globalMessage && "margin-class"}>
                        {children}
                    </StyledNav>
                )}
            </>
        );
    },
);

Breadcrumbs.defaultProps = {
    links: [],
    maxNoOfLinks: 4,
};

Breadcrumbs.displayName = "Breadcrumbs";
/** @component */
export default withHistory(connect(mapStateToProps, mapDispatchToProps)(Breadcrumbs));

export { Slash, StyledNav };
